<template>
  <div class="guide-page">
    <!-- Header -->
    <section class="page-header">
      <div class="container">
        <h1>使用指南</h1>
        <p>快速上手 MookNote，开始记录你的生活</p>
      </div>
    </section>

    <!-- Quick Start -->
    <section class="quick-start">
      <div class="container">
        <h2 class="section-title">快速开始</h2>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              <h3>下载安装</h3>
              <p>从官网下载 APK 文件，在 Android 设备上安装应用。</p>
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              <h3>添加记录</h3>
              <p>点击底部中间的"+"按钮，选择要添加的类型：观影、阅读或笔记，填写相关信息后保存。</p>
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              <h3>查看管理</h3>
              <p>在首页可以查看所有记录，使用顶部标签切换不同类型，点击筛选按钮查看特定状态的内容。</p>
            </div>
          </div>
          <div class="step">
            <div class="step-number">4</div>
            <div class="step-content">
              <h3>数据备份</h3>
              <p>定期使用本地备份或云备份功能，确保数据完整。可在"我的"页面找到备份选项。</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Detailed Guide -->
    <section class="detailed-guide">
      <div class="container">
        <h2 class="section-title">详细教程</h2>
        <div class="guide-tabs">
          <button
            v-for="tab in tabs"
            :key="tab.id"
            :class="['tab-btn', { active: activeTab === tab.id }]"
            @click="activeTab = tab.id"
          >
            {{ tab.name }}
          </button>
        </div>

        <div class="guide-content">
          <!-- Movies Guide -->
          <div v-if="activeTab === 'movies'" class="guide-panel">
            <div class="guide-section">
              <h3>添加观影记录</h3>
              <ol>
                <li>点击底部导航栏的"+"按钮</li>
                <li>选择"观影"类型</li>
                <li>填写电影名称（必填）</li>
                <li>选择观看状态：已看/在看/想看</li>
                <li>可选：添加年份、评分、观看日期、海报图片</li>
                <li>在笔记栏记录观影心得</li>
                <li>点击保存</li>
              </ol>
            </div>
            <div class="guide-section">
              <h3>管理观影记录</h3>
              <ul>
                <li><strong>筛选查看：</strong>在观影页面顶部，点击"已看"/"在看"/"想看"筛选不同状态的影片</li>
                <li><strong>编辑记录：</strong>长按某条记录，选择编辑可修改信息</li>
                <li><strong>删除记录：</strong>长按某条记录，选择删除，删除的记录会进入回收站</li>
                <li><strong>查看详情：</strong>点击任意影片卡片，查看完整信息和笔记</li>
              </ul>
            </div>
          </div>

          <!-- Reading Guide -->
          <div v-if="activeTab === 'reading'" class="guide-panel">
            <div class="guide-section">
              <h3>添加阅读记录</h3>
              <ol>
                <li>点击底部导航栏的"+"按钮</li>
                <li>选择"阅读"类型</li>
                <li>填写书籍名称（必填）</li>
                <li>选择阅读状态：已读/在读/准备读</li>
                <li>可选：添加作者、评分、阅读日期、封面图片</li>
                <li>在笔记栏记录读书心得</li>
                <li>点击保存</li>
              </ol>
            </div>
            <div class="guide-section">
              <h3>管理阅读记录</h3>
              <ul>
                <li><strong>筛选查看：</strong>在阅读页面顶部，点击"已读"/"在读"/"准备读"筛选不同状态的书籍</li>
                <li><strong>更新进度：</strong>对于"在读"的书籍，可以更新阅读进度</li>
                <li><strong>编辑记录：</strong>长按某条记录，选择编辑可修改信息</li>
                <li><strong>删除记录：</strong>长按某条记录，选择删除</li>
              </ul>
            </div>
          </div>

          <!-- Notes Guide -->
          <div v-if="activeTab === 'notes'" class="guide-panel">
            <div class="guide-section">
              <h3>添加笔记</h3>
              <ol>
                <li>点击底部导航栏的"+"按钮</li>
                <li>选择"笔记"类型</li>
                <li>输入笔记内容</li>
                <li>可选：添加标签进行分类</li>
                <li>可选：txt格式支持插入图片</li>
                <li>点击保存</li>
              </ol>
            </div>
            <div class="guide-section">
              <h3>笔记管理</h3>
              <ul>
                <li><strong>查看笔记：</strong>在笔记页面按时间倒序查看所有笔记</li>
                <li><strong>搜索笔记：</strong>使用顶部搜索按钮，输入关键词快速查找</li>
                <li><strong>标签筛选：</strong>点击标签可筛选相同标签的笔记</li>
                <li><strong>编辑笔记：</strong>点击笔记进入详情页，可编辑修改</li>
                <li><strong>分享笔记：</strong>支持将笔记导出为文本分享</li>
              </ul>
            </div>
          </div>

          <!-- Backup Guide -->
          <div v-if="activeTab === 'backup'" class="guide-panel">
            <div class="guide-section">
              <h3>本地备份</h3>
              <ol>
                <li>进入"我的"页面</li>
                <li>点击"本地备份"</li>
                <li>选择"立即备份"</li>
                <li>选择保存位置，备份文件将保存为 .zip 格式</li>
              </ol>
              <p><strong>恢复数据：</strong></p>
              <ol>
                <li>进入"本地备份"页面</li>
                <li>点击"选择备份文件"</li>
                <li>找到之前保存的备份文件</li>
                <li>确认恢复，应用将重启并加载备份数据</li>
              </ol>
            </div>
            <div class="guide-section">
              <h3>云备份（WebDAV）</h3>
              <p>云备份可以将数据同步到支持 WebDAV 的网盘。</p>
              <ol>
                <li>进入"我的"页面，点击"云备份"</li>
                <li>配置 WebDAV 服务器地址、用户名和密码</li>
                <li>点击"测试连接"验证配置</li>
                <li>保存配置后，点击"立即同步"</li>
              </ol>
              <p><strong>注意事项：</strong></p>
              <ul>
                <li>webdav备份暂时不支持自动备份，将在后续版本中添加</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="faq">
      <div class="container">
        <h2 class="section-title">常见问题</h2>
        <div class="faq-list">
          <div
            v-for="(item, index) in faqs"
            :key="index"
            :class="['faq-item', { active: activeFaq === index }]"
          >
            <div class="faq-question" @click="toggleFaq(index)">
              <span>{{ item.q }}</span>
              <span class="faq-icon">{{ activeFaq === index ? '−' : '+' }}</span>
            </div>
            <div class="faq-answer" v-show="activeFaq === index">
              <p>{{ item.a }}</p>
            </div>
          </div>
        </div>
      </div>
    </section>


  </div>
</template>

<script>
export default {
  name: 'Guide',
  data() {
    return {
      activeTab: 'movies',
      activeFaq: null,
      tabs: [
        { id: 'movies', name: '观影记录' },
        { id: 'reading', name: '阅读管理' },
        { id: 'notes', name: '笔记功能' },
        { id: 'backup', name: '数据备份' }
      ],
      faqs: [
        {
          q: 'MookNote 是免费的吗？',
          a: '是的，MookNote 完全免费，没有任何内购或订阅费用。'
        },
        {
          q: '数据存储在哪里？',
          a: '所有数据都存储在您的设备本地，使用 SQLite 数据库。我们不会上传任何数据到服务器。'
        },
        {
          q: '如何迁移数据到新设备？',
          a: '使用本地备份功能导出数据，然后在新设备上安装 MookNote，通过本地备份恢复数据。或者使用 WebDAV 云备份功能同步数据。'
        },
        {
          q: '删除的记录可以恢复吗？',
          a: '可以。删除的记录会先进入回收站，在"我的"-"回收站"中可以恢复。清空回收站后，所有数据将无法恢复。'
        },
        {
          q: '如何导出数据？',
          a: '使用本地备份功能可以将数据库导出到指download/mooknote路径下的zip文件。未来版本将支持更多导出格式，如 pdf，csv，json 等。'
        }
      ]
    }
  },
  methods: {
    toggleFaq(index) {
      this.activeFaq = this.activeFaq === index ? null : index
    }
  }
}
</script>

<style scoped>
/* Page Header */
.page-header {
  padding: 100px 24px 60px;
  background: var(--color-bg-secondary);
  text-align: center;
}

.page-header h1 {
  font-size: 42px;
  font-weight: 700;
  margin-bottom: 12px;
  color: var(--color-text);
}

.page-header p {
  font-size: 17px;
  color: var(--color-text-secondary);
}

/* Quick Start */
.quick-start {
  padding: 80px 24px;
  background: white;
}

.steps {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 32px;
  margin-top: 48px;
}

.step {
  display: flex;
  gap: 20px;
}

.step-number {
  width: 48px;
  height: 48px;
  background: var(--color-text);
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-weight: 700;
  flex-shrink: 0;
}

.step-content h3 {
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 8px;
  color: var(--color-text);
}

.step-content p {
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.7;
}

/* Detailed Guide */
.detailed-guide {
  padding: 80px 24px;
  background: var(--color-bg-secondary);
}

.guide-tabs {
  display: flex;
  gap: 12px;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 48px;
  margin-bottom: 40px;
}

.tab-btn {
  padding: 12px 24px;
  border: none;
  background: white;
  border-radius: 8px;
  font-size: 15px;
  font-weight: 500;
  color: var(--color-text-secondary);
  cursor: pointer;
  transition: all 0.2s;
}

.tab-btn:hover {
  background: var(--color-bg-secondary);
}

.tab-btn.active {
  background: var(--color-text);
  color: white;
}

.guide-content {
  max-width: 800px;
  margin: 0 auto;
}

.guide-panel {
  background: white;
  border-radius: 16px;
  padding: 40px;
}

.guide-section {
  margin-bottom: 40px;
}

.guide-section:last-child {
  margin-bottom: 0;
}

.guide-section h3 {
  font-size: 20px;
  font-weight: 600;
  margin-bottom: 20px;
  color: var(--color-text);
  padding-bottom: 12px;
  border-bottom: 1px solid var(--color-border);
}

.guide-section ol,
.guide-section ul {
  padding-left: 24px;
  color: var(--color-text-secondary);
  line-height: 2;
}

.guide-section li {
  margin-bottom: 8px;
}

.guide-section strong {
  color: var(--color-text);
}

.guide-section p {
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin-bottom: 16px;
}

/* FAQ */
.faq {
  padding: 80px 24px;
  background: white;
}

.faq-list {
  max-width: 800px;
  margin: 48px auto 0;
}

.faq-item {
  border-bottom: 1px solid var(--color-border);
}

.faq-question {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 0;
  cursor: pointer;
  font-size: 16px;
  font-weight: 500;
  color: var(--color-text);
  transition: color 0.2s;
}

.faq-question:hover {
  color: var(--color-text-secondary);
}

.faq-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  color: var(--color-text-muted);
}

.faq-answer {
  padding-bottom: 24px;
}

.faq-answer p {
  color: var(--color-text-secondary);
  line-height: 1.8;
}

/* Tips */
.tips {
  padding: 80px 24px;
  background: var(--color-bg-secondary);
}

.tips-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 24px;
  margin-top: 48px;
}

.tip-card {
  background: white;
  padding: 32px;
  border-radius: 16px;
  text-align: center;
}

.tip-icon {
  width: 56px;
  height: 56px;
  background: var(--color-bg-secondary);
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  margin: 0 auto 20px;
}

.tip-card h3 {
  font-size: 17px;
  font-weight: 600;
  margin-bottom: 10px;
  color: var(--color-text);
}

.tip-card p {
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.7;
}

/* Responsive */
@media (max-width: 768px) {
  .page-header h1 {
    font-size: 32px;
  }

  .guide-tabs {
    gap: 8px;
  }

  .tab-btn {
    padding: 10px 16px;
    font-size: 14px;
  }

  .guide-panel {
    padding: 24px;
  }

  .step {
    flex-direction: column;
    text-align: center;
  }
}
</style>
